import { useQuery } from '@apollo/client'
import React, { useCallback, useEffect, useState } from 'react'
import { GET_BOT_FORM_RESPONSE, GET_BOT_FORM_RESPONSE_BY_DATE_RANGE } from '../../graphql/query'
import CsvDownloader from 'react-csv-downloader'

// Type definitions
type TicketType =
  | 'action-ticket-more-details'
  | 'action-pending-tickets'
  | 'action-user-login'
  | 'action-file-upload'
  | 'action-user-solman-details'
  | 'action-question-answer-2'
  | 'action-level-3-3'
  | 'ad_action-level-2-2'
  | 'ad_action-level-1-1'
  | 'action-user-vecv-login'

const typeOptions = [
  { value: 'action-ticket-more-details', label: 'Ticket Details' },
  { value: 'action-pending-tickets', label: 'Pending Tickets' },
  { value: 'action-user-login', label: 'User Login' },
  { value: 'action-file-upload', label: 'File Upload' },
  { value: 'action-user-solman-details', label: 'Solman Details' },
  { value: 'action-question-answer-2', label: 'Question Answer' },
  { value: 'action-level-3-3', label: 'Level 3' },
  { value: 'ad_action-level-2-2', label: 'Action Level 2' },
  { value: 'ad_action-level-1-1', label: 'Action Level 1' },
  { value: 'action-user-vecv-login', label: 'Vecv Login' }
]

// CSS Variables
const theme = {
  colors: {
    bg: '#f1f5f9',
    card: '#ffffff',
    border: '#e2e8f0',
    borderLight: '#f1f5f9',
    primary: '#6366f1',
    primaryHover: '#4f46e5',
    success: '#10b981',
    successBg: '#ecfdf5',
    warning: '#f59e0b',
    warningBg: '#fffbeb',
    error: '#ef4444',
    errorBg: '#fef2f2',
    info: '#3b82f6',
    infoBg: '#eff6ff',
    cyan: '#06b6d4',
    cyanBg: '#ecfeff',
    purple: '#8b5cf6',
    purpleBg: '#f5f3ff',
    pink: '#ec4899',
    pinkBg: '#fdf2f8',
    textPrimary: '#0f172a',
    textSecondary: '#475569',
    textMuted: '#94a3b8',
    textLight: '#cbd5e1'
  },
  fonts: {
    sans: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
  },
  shadows: {
    sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    xl: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)'
  },
  radius: {
    sm: '6px',
    md: '8px',
    lg: '12px',
    xl: '16px'
  }
}

const TicketDetailsPage = () => {
  const [selectedType, setSelectedType] = useState<TicketType>('action-user-solman-details')
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [sortBy, setSortBy] = useState('createdAt')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [showModal, setShowModal] = useState(false)
  const [selectedItem, setSelectedItem] = useState<any>(null)
  const [showExportModal, setShowExportModal] = useState(false)
  const [exportLimit, setExportLimit] = useState(100)
  const [exportPage, setExportPage] = useState(1)

  const { data, loading, error, refetch } = useQuery(GET_BOT_FORM_RESPONSE, {
    variables: {
      input: {
        appIdString: '80ad0105236fab613e84ed7920801142',
        bgAccountId: 5,
        sortOrder: sortOrder,
        type: selectedType,
        page: 1,
        limit: rowsPerPage,
        sortBy: sortBy
      }
    }
  })

  const {
    data: excelData,
    loading: excelLoading,
    error: excelError,
    refetch: refetchExcel
  } = useQuery(GET_BOT_FORM_RESPONSE_BY_DATE_RANGE, {
    variables: {
      input: {
        endDate: dateTo,
        limit: exportLimit,
        page: exportPage,
        startDate: dateFrom
      }
    },
    skip: !dateFrom || !dateTo
  })

  console.log('excelData', excelData)

  const [filteredResponses, setFilteredResponses] = useState<any[]>([])
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  })

  useEffect(() => {
    if (data?.getbotFormResponse) {
      setFilteredResponses(data.getbotFormResponse.data)
      setPagination({
        page: data.getbotFormResponse.page,
        limit: data.getbotFormResponse.limit,
        total: data.getbotFormResponse.total,
        totalPages: data.getbotFormResponse.totalPages
      })
    }
  }, [data])

  const handlePageChange = useCallback(
    (newPage: number) => {
      refetch({
        input: {
          appIdString: '80ad0105236fab613e84ed7920801142',
          bgAccountId: 5,
          sortOrder: sortOrder,
          type: selectedType,
          page: newPage,
          limit: rowsPerPage,
          sortBy: sortBy
        }
      })
    },
    [rowsPerPage, refetch, selectedType, sortBy, sortOrder]
  )

  const handleTypeChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newType = e.target.value as TicketType
      setSelectedType(newType)
      refetch({
        input: {
          appIdString: '80ad0105236fab613e84ed7920801142',
          bgAccountId: 5,
          sortOrder: sortOrder,
          type: newType,
          page: 1,
          limit: rowsPerPage,
          sortBy: sortBy
        }
      })
    },
    [refetch, rowsPerPage, sortBy, sortOrder]
  )

  const handleSort = useCallback(
    (column: string) => {
      const newOrder = sortBy === column && sortOrder === 'asc' ? 'desc' : 'asc'
      setSortBy(column)
      setSortOrder(newOrder)
      refetch({
        input: {
          appIdString: '80ad0105236fab613e84ed7920801142',
          bgAccountId: 5,
          sortOrder: newOrder,
          type: selectedType,
          page: pagination.page,
          limit: rowsPerPage,
          sortBy: column
        }
      })
    },
    [refetch, selectedType, sortBy, sortOrder, pagination.page, rowsPerPage]
  )

  const handleRowsPerPageChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newLimit = Number(e.target.value)
      setRowsPerPage(newLimit)
      refetch({
        input: {
          appIdString: '80ad0105236fab613e84ed7920801142',
          bgAccountId: 5,
          sortOrder: sortOrder,
          type: selectedType,
          page: 1, // Reset to first page when changing rows per page
          limit: newLimit,
          sortBy: sortBy
        }
      })
    },
    [refetch, selectedType, sortBy, sortOrder]
  )

  const handleItemClick = (item: any) => {
    setSelectedItem(item)
    setShowModal(true)
  }

  const closeModal = () => {
    setShowModal(false)
    setSelectedItem(null)
  }

  const openExportModal = () => {
    setShowExportModal(true)
  }

  const closeExportModal = () => {
    setShowExportModal(false)
  }

  const handleExportDownload = () => {
    if (dateFrom && dateTo) {
      refetchExcel({
        input: {
          endDate: dateTo,
          limit: exportLimit,
          page: exportPage,
          startDate: dateFrom
        }
      })
    }
  }

  // Parse JSON strings
  const parseJson = (jsonString: any) => {
    try {
      if (typeof jsonString === 'object') return jsonString
      return JSON.parse(jsonString)
    } catch (e) {
      return jsonString
    }
  }

  // Format date
  const formatDate = (dateString: any) => {
    const date = new Date(dateString)
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  }

  // Format date for CSV export (no commas to avoid column splitting)
  const formatDateForCsv = (dateString: any) => {
    const date = new Date(dateString)
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    const seconds = String(date.getSeconds()).padStart(2, '0')
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`
  }

  // Extract ticket ID from result text if available
  const extractTicketId = (result: string) => {
    const match = result?.match(/IncidentId: (\d+)/)
    return match ? match[1] : 'N/A'
  }

  // Get status from result
  const getStatus = (result: string) => {
    if (result?.includes('Success')) return 'Success'
    if (result?.includes('Failed')) return 'Failed'
    return 'Incident'
  }

  // Status badge styles
  const getStatusStyle = (status: any) => {
    switch (status) {
      case 'Success':
        return { bg: theme.colors.successBg, color: theme.colors.success, label: 'Success' }
      case 'Failed':
        return { bg: theme.colors.errorBg, color: theme.colors.error, label: 'Failed' }
      default:
        return { bg: theme.colors.cyanBg, color: theme.colors.cyan, label: 'Incident' }
    }
  }
  // Get transaction type from payload
  const getTransactionType = (payload: any) => {
    if (!payload) return 'Incident Creation'
    const level1 = payload.level_1?.label || payload.level_1?.level1
    return level1 || 'Incident Creation'
  }

  // Get level 2 category
  const getLevel2 = (payload: any) => {
    if (!payload) return null
    return payload.level_2?.label || payload.level_2?.level2
  }

  // Get level 3 category
  const getLevel3 = (payload: any) => {
    if (!payload) return null
    return payload.level_3?.label || payload.level_3?.level3
  }

  // Extract level data dynamically
  const getLevelData = (payload: any) => {
    if (!payload) return []

    return Object.keys(payload)
      .filter((key) => key.startsWith('level_'))
      .sort()
      .map((key) => ({
        key,
        value: payload[key]
      }))
  }

  // Format the level key for display
  const formatLevelKey = (key: string) => {
    return key.replace('level_', 'Level ')
  }

  function stripHtmlTags(input: any) {
    const div = document.createElement('div')
    div.innerHTML = input
    return div.textContent || ''
  }

  const getCsvData = () => {
    console.log(filteredResponses, 'response')
    if (!excelData || excelData.length === 0) return []

    return excelData?.getbotFormResponseExcelData?.data?.records?.map(
      (item: any, index: number) => {
        const payload = parseJson(item.payload) || {}
        const tktDetails = payload.tkt_details || {}

        const flatPayload: any = {}

        flatPayload['S.No'] = index + 1
        flatPayload['Ticket ID'] = item.id
        flatPayload['Incident ID'] = extractTicketId(item.result)
        flatPayload['Chatroom ID'] = item.chatRoomId || 'N/A'
        flatPayload['Login User ID'] = item.loginUserId || 'N/A'
        flatPayload['Date Created'] = formatDateForCsv(item.createdAt)
        flatPayload['Status'] = getStatusStyle(item.result).label

        Object.keys(payload).forEach((key) => {
          if (key.startsWith('level_')) {
            const label = payload[key]?.label
            const levelValue = label || payload[key]?.[`level${key.split('_')[1]}`] || 'N/A'
            flatPayload[`Level ${key.split('_')[1]}`] = levelValue
          }
        })

        Object.entries(tktDetails).forEach(([key, value]) => {
          if (key !== 'label' && key !== 'type') {
            const formattedKey = key
              .replace(/([A-Z])/g, ' $1')
              .replace(/^./, (s) => s.toUpperCase())
            flatPayload[formattedKey] = value
          }
        })

        return flatPayload
      }
    )
  }

  const csvData = getCsvData()
  const csvHeaders =
    csvData.length > 0 ? Object.keys(csvData[0]).map((key) => ({ id: key, displayName: key })) : []

  // Loading skeleton
  if (loading) {
    return (
      <div style={{ backgroundColor: '#f5f7fa', minHeight: '100vh', padding: '24px' }}>
        <div
          style={{
            backgroundColor: '#fff',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
          }}
        >
          <div
            style={{
              height: '28px',
              width: '180px',
              backgroundColor: '#e8ecf1',
              borderRadius: '6px',
              marginBottom: '24px'
            }}
          />
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <div
              key={i}
              style={{
                height: '52px',
                backgroundColor: '#f5f7fa',
                borderRadius: '8px',
                marginBottom: '8px'
              }}
            />
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ backgroundColor: '#f5f7fa', minHeight: '100vh', padding: '24px' }}>
        <div
          style={{
            backgroundColor: '#fff',
            borderRadius: '16px',
            padding: '48px',
            textAlign: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
          }}
        >
          <div style={{ color: '#ef4444', fontSize: '16px' }}>
            Error loading data: {error.message}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={{ backgroundColor: '#f5f7fa', minHeight: '100vh', padding: '24px' }}>
      {/* Main Card */}
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '16px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 24px',
            borderBottom: '1px solid #eef1f5',
            flexWrap: 'wrap',
            gap: '16px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <h1 style={{ fontSize: '20px', fontWeight: 600, color: '#1a1f36', margin: 0 }}>
              Incident Tickets
            </h1>

            {/* Type Dropdown */}
            <select
              value={selectedType}
              onChange={handleTypeChange}
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '8px 12px',
                fontSize: '13px',
                color: '#374151',
                backgroundColor: '#fff',
                cursor: 'pointer',
                minWidth: '160px'
              }}
            >
              {typeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {/* Export Button */}
            <button
              onClick={openExportModal}
              style={{
                backgroundColor: '#22c55e',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 16px',
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s ease'
              }}
            >
              <svg width='14' height='14' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4'
                />
              </svg>
              Export Tickets
            </button>
          </div>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb' }}>
                <th
                  style={{
                    padding: '14px 16px',
                    textAlign: 'center',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '1px solid #eef1f5',
                    width: '60px'
                  }}
                >
                  S.No
                </th>
                <th
                  style={{
                    padding: '14px 20px',
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '1px solid #eef1f5'
                  }}
                >
                  Incident ID
                </th>
                <th
                  style={{
                    padding: '14px 16px',
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '1px solid #eef1f5'
                  }}
                >
                  Chatrom ID
                </th>
                <th
                  style={{
                    padding: '14px 16px',
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '1px solid #eef1f5',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleSort('userId')}
                >
                  User ID {sortBy === 'userId' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  style={{
                    padding: '14px 16px',
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '1px solid #eef1f5'
                  }}
                >
                  Transaction Type
                </th>
                <th
                  style={{
                    padding: '14px 16px',
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '1px solid #eef1f5',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleSort('status')}
                >
                  Status {sortBy === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  style={{
                    padding: '14px 16px',
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '1px solid #eef1f5',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleSort('createdAt')}
                >
                  Date & Time {sortBy === 'createdAt' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  style={{
                    padding: '14px 16px',
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '1px solid #eef1f5'
                  }}
                >
                  Result
                </th>
                <th
                  style={{
                    padding: '14px 16px',
                    textAlign: 'center',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '1px solid #eef1f5',
                    width: '60px'
                  }}
                ></th>
              </tr>
            </thead>
            <tbody>
              {filteredResponses.length > 0 ? (
                filteredResponses.map((item: any, index: number) => {
                  const payload = parseJson(item.payload) || {}
                  const status: any = getStatusStyle(item.result)
                  const transactionType = getTransactionType(payload)
                  const serialNumber = (pagination.page - 1) * pagination.limit + index + 1

                  // Badge colors for transaction type
                  const getTransactionBadgeStyle = (type: string) => {
                    const typeUpper = type?.toUpperCase() || ''
                    if (typeUpper.includes('HELP DESK')) {
                      return { backgroundColor: '#dcfce7', color: '#15803d' }
                    }
                    if (typeUpper.includes('USER MANAGEMENT')) {
                      return { backgroundColor: '#fef3c7', color: '#b45309' }
                    }
                    if (typeUpper.includes('PASSWORD')) {
                      return { backgroundColor: '#fce7f3', color: '#be185d' }
                    }
                    return { backgroundColor: '#e0f2fe', color: '#0369a1' }
                  }

                  // Badge colors for status
                  const getStatusBadgeStyle = (statusVal: string) => {
                    if (statusVal === 'Success') {
                      return { backgroundColor: '#dcfce7', color: '#15803d' }
                    }
                    if (statusVal === 'Failed') {
                      return { backgroundColor: '#fee2e2', color: '#dc2626' }
                    }
                    // Incident - teal/cyan
                    return { backgroundColor: '#cffafe', color: '#0891b2' }
                  }

                  return (
                    <tr
                      key={item.id}
                      style={{
                        borderBottom: '1px solid #f3f4f6',
                        cursor: 'pointer',
                        transition: 'background-color 0.15s'
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#fafbfc')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                      onClick={() => handleItemClick(item)}
                    >
                      <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                        <span style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>
                          {serialNumber}
                        </span>
                      </td>
                      <td style={{ padding: '14px 20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div
                            style={{
                              width: '28px',
                              height: '28px',
                              borderRadius: '6px',
                              backgroundColor: '#e0f2fe',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <svg
                              width='14'
                              height='14'
                              viewBox='0 0 24 24'
                              fill='none'
                              stroke='#0284c7'
                              strokeWidth='2'
                            >
                              <path d='M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z' />
                              <path d='M14 2v6h6' />
                              <path d='M16 13H8' />
                              <path d='M16 17H8' />
                              <path d='M10 9H8' />
                            </svg>
                          </div>
                          <span style={{ color: '#0284c7', fontWeight: 500, fontSize: '14px' }}>
                            {extractTicketId(item.result)}
                          </span>
                        </div>
                      </td>
                      <td style={{ padding: '14px 16px', fontSize: '14px', color: '#374151' }}>
                        {item.chatRoomId || 'N/A'}
                      </td>
                      <td style={{ padding: '14px 16px', fontSize: '14px', color: '#374151' }}>
                        {item.loginUserId || 'N/A'}
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '5px 12px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: 500,
                            ...getTransactionBadgeStyle(transactionType)
                          }}
                        >
                          {transactionType}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '5px 12px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: 500,
                            backgroundColor: status.bg,
                            color: status.color
                          }}
                        >
                          {status.label}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px', fontSize: '14px', color: '#374151' }}>
                        {formatDate(item.createdAt)}
                      </td>
                      <td
                        style={{
                          padding: '14px 16px',
                          fontSize: '13px',
                          color: '#374151',
                          maxWidth: '200px'
                        }}
                      >
                        <div
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                          title={stripHtmlTags(item.result)}
                        >
                          {stripHtmlTags(item.result)}
                        </div>
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                        <button
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#9ca3af',
                            cursor: 'pointer',
                            padding: '4px 8px',
                            fontSize: '16px',
                            letterSpacing: '2px'
                          }}
                          onClick={(e) => {
                            e.stopPropagation()
                            handleItemClick(item)
                          }}
                        >
                          •••
                        </button>
                      </td>
                    </tr>
                  )
                })
              ) : (
                <tr>
                  <td
                    colSpan={9}
                    style={{
                      padding: '48px',
                      textAlign: 'center',
                      color: '#9ca3af',
                      fontSize: '14px'
                    }}
                  >
                    No tickets found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 24px',
            borderTop: '1px solid #eef1f5',
            flexWrap: 'wrap',
            gap: '16px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <span style={{ fontSize: '13px', color: '#6b7280' }}>
              Page {pagination.page} of {pagination.totalPages}
            </span>

            {/* Rows per page dropdown */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ fontSize: '13px', color: '#6b7280' }}>Rows per page:</label>
              <select
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  padding: '6px 10px',
                  fontSize: '13px',
                  color: '#374151',
                  backgroundColor: '#fff',
                  cursor: 'pointer',
                  minWidth: '70px'
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {/* First */}
            <button
              onClick={() => handlePageChange(1)}
              disabled={pagination.page === 1}
              style={{
                width: '32px',
                height: '32px',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                backgroundColor: '#fff',
                color: pagination.page === 1 ? '#d1d5db' : '#6b7280',
                cursor: pagination.page === 1 ? 'not-allowed' : 'pointer',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {'<'}
            </button>
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              style={{
                width: '32px',
                height: '32px',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                backgroundColor: '#fff',
                color: pagination.page === 1 ? '#d1d5db' : '#6b7280',
                cursor: pagination.page === 1 ? 'not-allowed' : 'pointer',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {'<<'}
            </button>

            {/* Current page indicator */}
            <div
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '6px',
                backgroundColor: '#3b82f6',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: 500,
                margin: '0 4px'
              }}
            >
              ■
            </div>

            {/* Page numbers */}
            {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
              let pageNum: number
              if (pagination.totalPages <= 5) {
                pageNum = i + 1
              } else if (pagination.page <= 3) {
                pageNum = i + 1
              } else if (pagination.page >= pagination.totalPages - 2) {
                pageNum = pagination.totalPages - 4 + i
              } else {
                pageNum = pagination.page - 2 + i
              }

              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  style={{
                    width: '32px',
                    height: '32px',
                    border: pagination.page === pageNum ? 'none' : '1px solid #e5e7eb',
                    borderRadius: '6px',
                    backgroundColor: pagination.page === pageNum ? '#3b82f6' : '#fff',
                    color: pagination.page === pageNum ? '#fff' : '#6b7280',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: pagination.page === pageNum ? 500 : 400,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {pageNum}
                </button>
              )
            })}

            {/* Next */}
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.totalPages}
              style={{
                width: '32px',
                height: '32px',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                backgroundColor: '#fff',
                color: pagination.page === pagination.totalPages ? '#d1d5db' : '#6b7280',
                cursor: pagination.page === pagination.totalPages ? 'not-allowed' : 'pointer',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {'>'}
            </button>
            <button
              onClick={() => handlePageChange(pagination.totalPages)}
              disabled={pagination.page === pagination.totalPages}
              style={{
                width: '32px',
                height: '32px',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                backgroundColor: '#fff',
                color: pagination.page === pagination.totalPages ? '#d1d5db' : '#6b7280',
                cursor: pagination.page === pagination.totalPages ? 'not-allowed' : 'pointer',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {'>>'}
            </button>
          </div>
        </div>
      </div>

      {/* Modal */}

      {showModal && selectedItem && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '24px',
            backdropFilter: 'blur(4px)'
          }}
          onClick={closeModal}
        >
          <div
            style={{
              backgroundColor: theme.colors.card,
              borderRadius: theme.radius.xl,
              width: '100%',
              maxWidth: '560px',
              maxHeight: '90vh',
              overflow: 'hidden',
              boxShadow: theme.shadows.xl,
              animation: 'modalSlideIn 0.2s ease-out'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '20px 24px',
                borderBottom: `1px solid ${theme.colors.border}`
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: theme.radius.md,
                    backgroundColor: theme.colors.infoBg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <svg
                    width='20'
                    height='20'
                    fill='none'
                    stroke={theme.colors.info}
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                    />
                  </svg>
                </div>
                <div>
                  <h2
                    style={{
                      fontSize: '18px',
                      fontWeight: 600,
                      color: theme.colors.textPrimary,
                      margin: 0
                    }}
                  >
                    Incident Details
                  </h2>
                  <p style={{ fontSize: '13px', color: theme.colors.textMuted, margin: '2px 0 0' }}>
                    ID: {extractTicketId(selectedItem.result)}
                  </p>
                </div>
              </div>
              <button
                onClick={closeModal}
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: theme.radius.md,
                  border: 'none',
                  backgroundColor: 'transparent',
                  color: theme.colors.textMuted,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background-color 0.15s'
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = theme.colors.borderLight)
                }
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <svg width='20' height='20' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M6 18L18 6M6 6l12 12'
                  />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '24px', maxHeight: 'calc(90vh - 160px)', overflowY: 'auto' }}>
              {/* Info Grid */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '20px',
                  marginBottom: '24px'
                }}
              >
                <div>
                  <label
                    style={{
                      fontSize: '12px',
                      fontWeight: 500,
                      color: theme.colors.textMuted,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}
                  >
                    Chatroom ID
                  </label>
                  <p
                    style={{
                      fontSize: '15px',
                      fontWeight: 500,
                      color: theme.colors.info,
                      margin: '6px 0 0'
                    }}
                  >
                    {selectedItem.chatRoomId || '—'}
                  </p>
                </div>
                <div>
                  <label
                    style={{
                      fontSize: '12px',
                      fontWeight: 500,
                      color: theme.colors.textMuted,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}
                  >
                    Created
                  </label>
                  <p
                    style={{ fontSize: '15px', color: theme.colors.textPrimary, margin: '6px 0 0' }}
                  >
                    {formatDate(selectedItem.createdAt)}
                  </p>
                </div>
                <div>
                  <label
                    style={{
                      fontSize: '12px',
                      fontWeight: 500,
                      color: theme.colors.textMuted,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}
                  >
                    User ID
                  </label>
                  <p
                    style={{ fontSize: '15px', color: theme.colors.textPrimary, margin: '6px 0 0' }}
                  >
                    {selectedItem.loginUserId || '—'}
                  </p>
                </div>
                <div>
                  <label
                    style={{
                      fontSize: '12px',
                      fontWeight: 500,
                      color: theme.colors.textMuted,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}
                  >
                    Status
                  </label>
                  <div style={{ marginTop: '6px' }}>
                    {(() => {
                      const statusStyle = getStatusStyle(selectedItem.result)
                      return (
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '5px 10px',
                            borderRadius: '20px',
                            fontSize: '12px',
                            fontWeight: 500,
                            backgroundColor: statusStyle.bg,
                            color: statusStyle.color
                          }}
                        >
                          <span
                            style={{
                              width: '6px',
                              height: '6px',
                              borderRadius: '50%',
                              backgroundColor: statusStyle.color
                            }}
                          />
                          {statusStyle.label}
                        </span>
                      )
                    })()}
                  </div>
                </div>
              </div>

              {/* Categorization */}
              <div style={{ marginBottom: '24px' }}>
                <h4
                  style={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: theme.colors.textSecondary,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '12px'
                  }}
                >
                  Categorization
                </h4>
                <div
                  style={{
                    backgroundColor: theme.colors.borderLight,
                    borderRadius: theme.radius.lg,
                    padding: '16px'
                  }}
                >
                  {getLevelData(parseJson(selectedItem.payload)).map(
                    (levelItem: any, index: number) => {
                      const label =
                        levelItem.value?.label || levelItem.value?.[`level${index + 1}`] || '—'
                      const colors = [
                        { bg: theme.colors.infoBg, color: theme.colors.info },
                        { bg: theme.colors.warningBg, color: theme.colors.warning },
                        { bg: theme.colors.pinkBg, color: theme.colors.pink }
                      ]
                      const colorStyle = colors[index] || colors[0]

                      return (
                        <div
                          key={index}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '10px 0',
                            borderBottom:
                              index < getLevelData(parseJson(selectedItem.payload)).length - 1
                                ? `1px solid ${theme.colors.border}`
                                : 'none'
                          }}
                        >
                          <span style={{ fontSize: '14px', color: theme.colors.textSecondary }}>
                            {formatLevelKey(levelItem.key)}
                          </span>
                          <span
                            style={{
                              padding: '4px 12px',
                              borderRadius: '20px',
                              fontSize: '12px',
                              fontWeight: 600,
                              backgroundColor: colorStyle.bg,
                              color: colorStyle.color,
                              textTransform: 'uppercase'
                            }}
                          >
                            {label}
                          </span>
                        </div>
                      )
                    }
                  )}
                </div>
              </div>

              {/* Result */}
              <div>
                <h4
                  style={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: theme.colors.textSecondary,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '12px'
                  }}
                >
                  Result
                </h4>
                <div
                  style={{
                    backgroundColor: theme.colors.borderLight,
                    borderRadius: theme.radius.lg,
                    padding: '16px',
                    fontSize: '14px',
                    color: theme.colors.textSecondary,
                    lineHeight: 1.6
                  }}
                >
                  {stripHtmlTags(selectedItem.result)}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div
              style={{
                padding: '16px 24px',
                borderTop: `1px solid ${theme.colors.border}`,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px'
              }}
            >
              <button
                onClick={closeModal}
                style={{
                  padding: '10px 20px',
                  borderRadius: theme.radius.md,
                  border: `1px solid ${theme.colors.border}`,
                  backgroundColor: theme.colors.card,
                  color: theme.colors.textSecondary,
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'background-color 0.15s'
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = theme.colors.borderLight)
                }
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = theme.colors.card)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '24px',
            backdropFilter: 'blur(4px)'
          }}
          onClick={closeExportModal}
        >
          <div
            style={{
              backgroundColor: theme.colors.card,
              borderRadius: theme.radius.xl,
              width: '100%',
              maxWidth: '480px',
              overflow: 'hidden',
              boxShadow: theme.shadows.xl,
              animation: 'modalSlideIn 0.2s ease-out'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Export Modal Header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '20px 24px',
                borderBottom: `1px solid ${theme.colors.border}`
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: theme.radius.md,
                    backgroundColor: theme.colors.successBg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <svg
                    width='20'
                    height='20'
                    fill='none'
                    stroke={theme.colors.success}
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4'
                    />
                  </svg>
                </div>
                <div>
                  <h2
                    style={{
                      fontSize: '18px',
                      fontWeight: 600,
                      color: theme.colors.textPrimary,
                      margin: 0
                    }}
                  >
                    Export Tickets
                  </h2>
                  <p style={{ fontSize: '13px', color: theme.colors.textMuted, margin: '2px 0 0' }}>
                    Configure export options
                  </p>
                </div>
              </div>
              <button
                onClick={closeExportModal}
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: theme.radius.md,
                  border: 'none',
                  backgroundColor: 'transparent',
                  color: theme.colors.textMuted,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background-color 0.15s'
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = theme.colors.borderLight)
                }
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <svg width='20' height='20' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M6 18L18 6M6 6l12 12'
                  />
                </svg>
              </button>
            </div>

            {/* Export Modal Body */}
            <div style={{ padding: '24px' }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '16px',
                  marginBottom: '20px'
                }}
              >
                {/* Date From */}
                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '13px',
                      fontWeight: 500,
                      color: theme.colors.textSecondary,
                      marginBottom: '8px'
                    }}
                  >
                    Date From
                  </label>
                  <input
                    type='date'
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    style={{
                      width: '100%',
                      border: `1px solid ${theme.colors.border}`,
                      borderRadius: theme.radius.md,
                      padding: '10px 12px',
                      fontSize: '14px',
                      color: theme.colors.textPrimary,
                      backgroundColor: '#fff',
                      cursor: 'pointer',
                      outline: 'none',
                      transition: 'border-color 0.15s'
                    }}
                  />
                </div>

                {/* Date To */}
                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '13px',
                      fontWeight: 500,
                      color: theme.colors.textSecondary,
                      marginBottom: '8px'
                    }}
                  >
                    Date To
                  </label>
                  <input
                    type='date'
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    style={{
                      width: '100%',
                      border: `1px solid ${theme.colors.border}`,
                      borderRadius: theme.radius.md,
                      padding: '10px 12px',
                      fontSize: '14px',
                      color: theme.colors.textPrimary,
                      backgroundColor: '#fff',
                      cursor: 'pointer',
                      outline: 'none',
                      transition: 'border-color 0.15s'
                    }}
                  />
                </div>

                {/* Limit */}
                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '13px',
                      fontWeight: 500,
                      color: theme.colors.textSecondary,
                      marginBottom: '8px'
                    }}
                  >
                    Limit
                  </label>
                  <input
                    type='number'
                    value={exportLimit}
                    onChange={(e) => setExportLimit(Number(e.target.value))}
                    min={1}
                    max={10000}
                    style={{
                      width: '100%',
                      border: `1px solid ${theme.colors.border}`,
                      borderRadius: theme.radius.md,
                      padding: '10px 12px',
                      fontSize: '14px',
                      color: theme.colors.textPrimary,
                      backgroundColor: '#fff',
                      outline: 'none',
                      transition: 'border-color 0.15s'
                    }}
                  />
                </div>

                {/* Page */}
                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '13px',
                      fontWeight: 500,
                      color: theme.colors.textSecondary,
                      marginBottom: '8px'
                    }}
                  >
                    Page
                  </label>
                  <input
                    type='number'
                    value={exportPage}
                    onChange={(e) => setExportPage(Number(e.target.value))}
                    min={1}
                    style={{
                      width: '100%',
                      border: `1px solid ${theme.colors.border}`,
                      borderRadius: theme.radius.md,
                      padding: '10px 12px',
                      fontSize: '14px',
                      color: theme.colors.textPrimary,
                      backgroundColor: '#fff',
                      outline: 'none',
                      transition: 'border-color 0.15s'
                    }}
                  />
                </div>
              </div>

              {/* Info Message for Page and Limit */}
              <div
                style={{
                  padding: '12px 16px',
                  backgroundColor: '#eff6ff',
                  borderRadius: theme.radius.md,
                  fontSize: '13px',
                  color: theme.colors.primary,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '8px',
                  marginBottom: '12px'
                }}
              >
                <svg
                  width='16'
                  height='16'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                  style={{ flexShrink: 0, marginTop: '1px' }}
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                  />
                </svg>
                <span>
                  To export more data, adjust <strong>Limit</strong> (records per page) and{' '}
                  <strong>Page</strong> (starting from 1) settings. Maximum limit is 10,000 records
                  per export.
                </span>
              </div>

              {/* Validation Message for Dates */}
              {(!dateFrom || !dateTo) && (
                <div
                  style={{
                    padding: '12px 16px',
                    backgroundColor: theme.colors.warningBg,
                    borderRadius: theme.radius.md,
                    fontSize: '13px',
                    color: theme.colors.warning,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '8px'
                  }}
                >
                  <svg width='16' height='16' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
                    />
                  </svg>
                  Please select both Date From and Date To
                </div>
              )}

              {/* Validation Message for Limit and Page */}
              {(exportLimit < 1 || exportPage < 1) && (
                <div
                  style={{
                    padding: '12px 16px',
                    backgroundColor: theme.colors.warningBg,
                    borderRadius: theme.radius.md,
                    fontSize: '13px',
                    color: theme.colors.warning,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <svg width='16' height='16' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
                    />
                  </svg>
                  Limit and Page must be at least 1
                </div>
              )}
            </div>

            {/* Export Modal Footer */}
            <div
              style={{
                padding: '16px 24px',
                borderTop: `1px solid ${theme.colors.border}`,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px'
              }}
            >
              <button
                onClick={closeExportModal}
                style={{
                  padding: '10px 20px',
                  borderRadius: theme.radius.md,
                  border: `1px solid ${theme.colors.border}`,
                  backgroundColor: theme.colors.card,
                  color: theme.colors.textSecondary,
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'background-color 0.15s'
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = theme.colors.borderLight)
                }
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = theme.colors.card)}
              >
                Cancel
              </button>

              {dateFrom && dateTo && exportLimit >= 1 && exportPage >= 1 ? (
                <CsvDownloader
                  filename={`Incident_Tickets_${dateFrom}_to_${dateTo}`}
                  extension='.csv'
                  separator=','
                  wrapColumnChar='"'
                  columns={csvHeaders}
                  datas={excelLoading ? [] : csvData}
                >
                  <button
                    onClick={handleExportDownload}
                    style={{
                      padding: '10px 20px',
                      borderRadius: theme.radius.md,
                      border: 'none',
                      backgroundColor: excelLoading ? '#9ca3af' : theme.colors.success,
                      color: '#fff',
                      fontSize: '14px',
                      fontWeight: 500,
                      cursor: excelLoading ? 'wait' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'background-color 0.15s'
                    }}
                    disabled={excelLoading}
                  >
                    {excelLoading ? (
                      <>
                        <div
                          className='spinner-border spinner-border-sm text-light'
                          role='status'
                          style={{ width: '14px', height: '14px' }}
                        >
                          <span className='sr-only'>Loading...</span>
                        </div>
                        Exporting...
                      </>
                    ) : (
                      <>
                        <svg
                          width='16'
                          height='16'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4'
                          />
                        </svg>
                        Download Report
                      </>
                    )}
                  </button>
                </CsvDownloader>
              ) : (
                <button
                  style={{
                    padding: '10px 20px',
                    borderRadius: theme.radius.md,
                    border: 'none',
                    backgroundColor: '#9ca3af',
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  disabled
                >
                  <svg width='16' height='16' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4'
                    />
                  </svg>
                  Download Report
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TicketDetailsPage
